import { prefersReducedMotion, useHostHover } from "./useHostHover";

const EASE = "cubic-bezier(0.16, 1, 0.3, 1)";

export type KanbanIconProps = {
  size?: number;
  /** Card stroke colour. Defaults to puntt-blue-12. */
  color?: string;
  /** Card fill — must match the surface behind the icon so cards occlude cleanly. */
  bg?: string;
  /** Animation duration in ms. */
  duration?: number;
  className?: string;
  /** Drive hover from the parent. Omit to follow the host `<a>` / `<button>`. */
  hover?: boolean;
  "data-testid"?: string;
};

/**
 * Projects / board.
 * Rest: five stroked cards implying three columns (2 / 1 / 2).
 * Hover: the lower card of column one slides right into column two and widens
 * to that column's width, landing flush beneath the card already there.
 *
 * The mover is the LAST child on purpose — it must paint over the other cards
 * while it travels.
 */
export function KanbanIcon({
  size = 18,
  color = "rgb(var(--puntt-blue-12))",
  bg = "#ffffff",
  duration = 420,
  className,
  hover: hoverProp,
  "data-testid": testId,
}: KanbanIconProps) {
  const { ref, hover } = useHostHover(hoverProp);
  const reduceMotion = prefersReducedMotion();
  const motionMs = reduceMotion ? 0 : duration;
  const cardMoved = hover && !reduceMotion;

  // why: block + line-height 0 — inline-flex inherited the size-3 24px line-height and made these rows 6.5px taller than New, sitting the glyph high
  return (
    <span
      ref={ref}
      className={className}
      data-testid={testId}
      data-hovered={hover ? "true" : "false"}
      aria-hidden="true"
      style={{
        display: "block",
        width: size,
        height: size,
        lineHeight: "0",
      }}
    >
      <svg
        viewBox="0 0 18 18"
        width={size}
        height={size}
        fill={bg}
        stroke={color}
        strokeWidth="1.125"
        strokeLinejoin="round"
        style={{ display: "block" }}
      >
        {/* column 1, upper */}
        <rect x="2.8125" y="3.9375" width="3.9375" height="4.5" rx="0.5625" />
        {/* column 2 */}
        <rect x="6.75" y="3.9375" width="4.5" height="6.75" rx="0.5625" />
        {/* column 3 */}
        <rect x="11.25" y="3.9375" width="3.9375" height="4.5" rx="0.5625" />
        <rect x="11.25" y="8.4375" width="3.9375" height="6.75" rx="0.5625" />
        {/* why: last child so the mover paints over the cards it crosses */}
        <rect
          x="2.8125"
          y="8.4375"
          width="3.9375"
          height="4.5"
          rx="0.5625"
          data-kanban="mover"
          style={{
            transformBox: "fill-box",
            transformOrigin: "0 0",
            transition: `transform ${motionMs}ms ${EASE}`,
            transform: cardMoved
              ? "translate(3.9375px, 2.25px) scaleX(1.1428)"
              : "translate(0px, 0px) scaleX(1)",
          }}
        />
      </svg>
    </span>
  );
}
